import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import ChatAgent from './components/ChatAgent';
import Visitors from './pages/Visitors';
import { trackVisit } from './utils/visitTracker';
import ErrorBoundary from './components/ErrorBoundary';
import Browse from './pages/Browse';
import Movies from './pages/Movies';
import Photos from './pages/Photos';
import Forecast from './pages/Forecast';
import Rote from './pages/Rote';
import SwellMap from './pages/SwellMap';
const Globe3D = React.lazy(() => import('./pages/Globe3D'));
import Costs from './pages/Costs';
import Docs from './pages/Docs';
import Jobs from './pages/Jobs';
import Watch from './pages/Watch';
import WatchAsset from './pages/WatchAsset';
import EditMovie from './pages/EditMovie';
import EditClip from './pages/EditClip';
import PhotoEditor from './pages/PhotoEditor';
import ClipGradeSession from './pages/ClipGradeSession';
import ProofSheet from './pages/ProofSheet';
import Trips from './pages/Trips';
import TripDetail from './pages/TripDetail';
import DropUpload from './pages/DropUpload';
import Login from './components/Login';
import { useAuth } from './AuthContext';
import { startGradingConfigSync } from './data/gradingConfigSync';
import { startUpscaleConfigSync } from './data/upscaleConfigSync';
import SITE from './site';
import { useNihiColorTheme } from './nihiTheme';

// On nihi-themed sites, the sand (light) palette only applies on public/
// visitor routes when the user has chosen light. Dark is the default.
// Admin tooling (editors, proofs, jobs…) always keeps the dark studio theme.
const LIGHT_THEME_ROUTES =
  /^\/($|movies$|forecast$|rote$|swell-map$|globe$|trips|watch\/|login$)/;

// Gate for admin-only pages (upload, editing, costs, processes, docs). Visitors
// can browse the public catalog freely; protected pages prompt for sign-in.
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="cs-auth-wrap">Loading…</div>;
  if (!user) {
    return (
      <div className="cs-auth-wrap">
        <Login />
      </div>
    );
  }
  return children;
}

export default function App() {
  const { user } = useAuth();
  const location = useLocation();

  // Keep the live auto-grade tuning (config/grading) in sync so the editor's
  // taming/reference reflect saved knobs instantly — no deploy needed.
  React.useEffect(() => startGradingConfigSync(), []);
  // Live upscaler/model + pipeline settings (config/upscale) — tuned in-app, read
  // by the GPU drainer per job, so model knobs change with no deploy.
  React.useEffect(() => startUpscaleConfigSync(), []);

  // Visitor tracking: one same-origin ping per route change (throttled per
  // path) to the /__visit Cloud Function, which records IP/geo/device into
  // this site's own `visitors` collection for the admin /visitors page.
  React.useEffect(() => {
    trackVisit(location.pathname, user);
  }, [location.pathname, user]);

  const { theme: nihiColorTheme } = useNihiColorTheme();
  React.useEffect(() => {
    const light =
      SITE.theme === 'nihi' &&
      nihiColorTheme === 'light' &&
      LIGHT_THEME_ROUTES.test(location.pathname);
    document.body.classList.toggle('nihi-light', light);
  }, [location.pathname, nihiColorTheme]);

  // Public guest-upload route is reachable without signing in. It must be
  // checked before anything else so videographers with a link can upload.
  const isDropRoute = /^\/u\//.test(window.location.pathname);
  if (isDropRoute) {
    return (
      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="/u/:token" element={<DropUpload />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  return (
    <>
      <Navbar />
      {/* Floating knowledge chat (RAG over the `knowledge` corpus) — renders
          only for signed-in users on sites with their own backend. */}
      <ChatAgent />
      <ErrorBoundary key={location.pathname}>
      <Routes>
        <Route path="/u/:token" element={<DropUpload />} />

        {/* ---- Public (no sign-in required) ---- */}
        <Route
          path="/"
          element={
            SITE.home.page === 'rote' ? <Rote />
              : SITE.home.page === 'swellmap' ? <SwellMap />
                : <Browse />
          }
        />
        <Route path="/movies" element={<Movies />} />
        <Route path="/forecast" element={<Forecast />} />
        {/* Rote is nihirote-only (its homepage). Other sites don't route it. */}
        {SITE.hasRote && (
          <Route path="/rote" element={SITE.home.page === 'rote' ? <Navigate to="/" replace /> : <Rote />} />
        )}
        <Route path="/swell-map" element={SITE.home.page === 'swellmap' ? <Navigate to="/" replace /> : <SwellMap />} />
        <Route path="/globe" element={<React.Suspense fallback={<div className="cs-globe-msg">Loading globe…</div>}><Globe3D /></React.Suspense>} />
        <Route path="/trips" element={<Trips />} />
        <Route path="/trips/:id" element={<TripDetail />} />
        <Route path="/watch/:id" element={<Watch />} />
        <Route path="/watch/asset/:tripId/:assetId" element={<WatchAsset />} />
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <div className="cs-auth-wrap"><Login /></div>} />

        {/* ---- Admin-only (sign-in required) ---- */}
        <Route path="/photos" element={<RequireAuth><Photos /></RequireAuth>} />
        <Route path="/costs" element={<RequireAuth><Costs /></RequireAuth>} />
        <Route path="/visitors" element={<RequireAuth><Visitors /></RequireAuth>} />
        <Route path="/jobs" element={<RequireAuth><Jobs /></RequireAuth>} />
        <Route path="/docs" element={<RequireAuth><Docs /></RequireAuth>} />
        <Route path="/clip/:tripId/:assetId/color" element={<RequireAuth><EditClip /></RequireAuth>} />
        <Route path="/movies/:id/edit" element={<RequireAuth><EditMovie /></RequireAuth>} />
        <Route path="/movies/:id/studio" element={<RequireAuth><EditMovie /></RequireAuth>} />
        <Route path="/movies/:id/social" element={<RequireAuth><EditMovie /></RequireAuth>} />
        <Route path="/movies/:id/color" element={<RequireAuth><EditMovie /></RequireAuth>} />
        <Route path="/photo/:tripId/:assetId" element={<RequireAuth><PhotoEditor /></RequireAuth>} />
        {/* Consolidated proofs hub; /proof-sheet kept for old deep links
            (?page=<sheetId> works on both). Old /proofs bookmarks land on the
            hub — the clip sessions live under ?page=sessions. */}
        <Route path="/proofs" element={<RequireAuth><ProofSheet /></RequireAuth>} />
        <Route path="/proof-sheet" element={<RequireAuth><ProofSheet /></RequireAuth>} />
        <Route path="/clip-grade/:tripId/:clipId" element={<RequireAuth><ClipGradeSession /></RequireAuth>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>
    </>
  );
}
